/**
 * LiveSessions — supervisor / office view.
 *
 * Two states:
 *   1. Session list — polls /api/live/presence every 2 s so the roster
 *      of active field surveyors is fresh. Each row shows tool,
 *      session name, operator, freshness dot, and quick counters.
 *   2. Session viewer — polls the underlying tool session every 2 s
 *      and re-uses the same visualisations the field user sees
 *      (SectionView / PlaneView for Ovality, FaceView / PlanView for
 *      Verticality). No measurement controls — purely observational.
 */

import React, { useEffect, useMemo, useState } from "react";
import { ArrowClockwise, Radio, User, Circle } from "@phosphor-icons/react";
import { live, ovality as ovalApi, verticality as vertApi, fmt } from "../lib/api";
import { SectionView, PlaneView } from "./OvalityCheck";
import { FaceView, PlanView } from "./VerticalityCheck";

const LIST_POLL_MS = 2000;
const SESSION_POLL_MS = 2000;
// A row is drawn "green" only if last_seen is within this window.
const FRESH_SEC = 8;

// ---------- Local Stat card (shared by both tool viewers) --------------

const Stat = ({ label, value, tone }) => {
  const color =
    tone === "ok" ? "#8DC63F" :
    tone === "warn" ? "#FF9F0A" :
    tone === "fail" ? "#FF3B30" : "#FFFFFF";
  return (
    <div className="rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2">
      <div className="text-[9px] uppercase tracking-[0.25em] text-[#8B939A]">{label}</div>
      <div className="mt-0.5 font-mono text-base" style={{ color }}>{value}</div>
    </div>
  );
};

// ---------- Session list -----------------------------------------------

const humanAgo = (secs) => {
  if (secs < 2) return "just now";
  if (secs < 60) return `${Math.round(secs)}s ago`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
};

const toolLabel = { ovality: "Ovality & Flatness", verticality: "Verticality" };
const toolColor = { ovality: "#00E1C7", verticality: "#7DD3FC" };

const SessionRow = ({ row, serverTime, onOpen }) => {
  const ageSec = Math.max(0, serverTime - row.last_seen);
  const fresh = ageSec <= FRESH_SEC;
  const shotsN = row.counters?.shots ?? 0;
  const refsN = row.counters?.refs ?? 0;
  const tone = toolColor[row.tool] || "#FFFFFF";
  return (
    <button
      data-testid={`live-row-${row.tool}-${row.session_id}`}
      onClick={onOpen}
      className="group flex w-full items-center justify-between rounded-lg border border-[#2A2E39] bg-[#14161A] p-4 text-left transition-colors hover:border-[#009E8E]"
    >
      <div className="flex items-center gap-4">
        {/* Freshness dot — pulses when heartbeat is live */}
        <div className="relative">
          <Circle
            size={14}
            weight="fill"
            style={{ color: fresh ? "#8DC63F" : "#4A5054" }}
          />
          {fresh && (
            <div
              className="absolute inset-0 animate-ping rounded-full"
              style={{ background: "#8DC63F", opacity: 0.35 }}
            />
          )}
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.25em]" style={{ color: tone }}>
            {toolLabel[row.tool] || row.tool}
          </div>
          <div
            className="mt-0.5 text-lg font-bold uppercase tracking-tight text-white"
            style={{ fontFamily: "Chakra Petch, sans-serif" }}
          >
            {row.session_name || row.session_id.slice(0, 8)}
          </div>
          <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#8B939A]">
            <User size={11} weight="bold" /> {row.username || "unknown"}
            <span className="text-[#4A5054]">·</span>
            <span>{humanAgo(ageSec)}</span>
          </div>
        </div>
      </div>
      <div className="hidden gap-4 text-right sm:flex">
        <div>
          <div className="text-[9px] uppercase tracking-[0.25em] text-[#8B939A]">Shots</div>
          <div className="font-mono text-lg text-white">{shotsN}</div>
        </div>
        {row.tool === "ovality" && (
          <div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-[#8B939A]">Refs</div>
            <div className="font-mono text-lg text-white">{refsN}</div>
          </div>
        )}
        {row.counters?.ovality_mm != null && (
          <div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-[#8B939A]">Ovality</div>
            <div className="font-mono text-lg text-white">
              {fmt(row.counters.ovality_mm, 2)} mm
            </div>
          </div>
        )}
        {row.counters?.max_perp_mm != null && (
          <div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-[#8B939A]">Max ⊥</div>
            <div className="font-mono text-lg text-white">
              {fmt(row.counters.max_perp_mm, 2)} mm
            </div>
          </div>
        )}
      </div>
    </button>
  );
};

const SessionList = ({ onPick }) => {
  const [rows, setRows] = useState([]);
  const [serverTime, setServerTime] = useState(0);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await live.list();
        if (cancelled) return;
        setRows(r.sessions || []);
        setServerTime(r.server_time || 0);
        setError(null);
      } catch (e) {
        if (!cancelled) setError("Cannot reach the server");
      } finally {
        if (!cancelled) setReady(true);
      }
    };
    tick();
    const id = setInterval(tick, LIST_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (!ready) {
    return (
      <div className="p-8 text-center text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">
        Loading live sessions…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-[#FF3B30] bg-[#FF3B30]/10 p-4 text-[11px] text-[#FF9F9F]">
        {error}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-[#2A2E39] bg-[#14161A] p-8 text-center">
        <Radio size={48} weight="light" className="mx-auto mb-3 text-[#4A5054]" />
        <div className="text-sm uppercase tracking-widest text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
          No active sessions
        </div>
        <div className="mt-2 text-[11px] text-[#8B939A]">
          When a surveyor opens Ovality or Verticality on the tablet, their session
          will appear here within a few seconds.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="live-session-list">
      {rows.map((row) => (
        <SessionRow
          key={`${row.tool}:${row.session_id}`}
          row={row}
          serverTime={serverTime}
          onOpen={() => onPick(row)}
        />
      ))}
    </div>
  );
};

// ---------- Session viewer (read-only) ---------------------------------

const OvalityViewer = ({ session }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      <Stat label="Shots" value={session.shots?.length || 0} />
      <Stat
        label="Radius"
        value={session.radius_mm != null ? `${fmt(session.radius_mm, 2)} mm` : "—"}
      />
      <Stat
        label="Ovality"
        value={session.ovality_mm != null ? `${fmt(session.ovality_mm, 2)} mm` : "—"}
        tone={session.within_tol === false ? "fail" : session.within_tol ? "ok" : undefined}
      />
      <Stat
        label="Max radial dev"
        value={session.max_radial_dev_mm != null ? `${fmt(session.max_radial_dev_mm, 2)} mm` : "—"}
      />
    </div>
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="rounded-lg border border-[#2A2E39] bg-[#14161A] p-4">
        <div className="mb-3 text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">Section</div>
        <SectionView session={session} />
      </div>
      <div className="rounded-lg border border-[#2A2E39] bg-[#14161A] p-4">
        <div className="mb-3 text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">Flatness</div>
        <PlaneView session={session} planeMode="fit" />
      </div>
    </div>
  </div>
);

const VerticalityViewer = ({ session }) => {
  const shots = session.shots || [];
  const worst = shots
    .filter((s) => s.role === "check")
    .reduce((m, s) => Math.max(m, Math.abs(s.perpendicular_mm || 0)), 0);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Shots" value={shots.length} />
        <Stat
          label="Reference"
          value={session.reference_ready ? "Ready" : "Set base A + B"}
          tone={session.reference_ready ? "ok" : "warn"}
        />
        <Stat
          label="Tolerance"
          value={`± ${fmt(session.tolerance_mm, 1)} mm`}
        />
        <Stat
          label="Worst deviation"
          value={`${fmt(worst, 2)} mm`}
          tone={worst > session.tolerance_mm ? "fail" : worst > 0 ? "ok" : undefined}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-[#2A2E39] bg-[#14161A] p-4">
          <div className="mb-3 text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">Face view</div>
          <FaceView
            shots={shots}
            tolerance_mm={session.tolerance_mm}
            referenceReady={session.reference_ready}
          />
        </div>
        <div className="rounded-lg border border-[#2A2E39] bg-[#14161A] p-4">
          <div className="mb-3 text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">Plan view</div>
          <PlanView
            shots={shots}
            tolerance_mm={session.tolerance_mm}
            session={session}
          />
        </div>
      </div>
      {session.warning && (
        <div className="rounded-md border border-[#FF9F0A] bg-[#FF9F0A]/10 px-3 py-2 text-[11px] text-[#FFC989]">
          ⚠ {session.warning}
        </div>
      )}
    </div>
  );
};

const SessionViewer = ({ picked, onBack }) => {
  const [session, setSession] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(0);
  const [error, setError] = useState(null);

  const api = picked.tool === "ovality" ? ovalApi : vertApi;

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await api.get(picked.session_id);
        if (cancelled) return;
        setSession(s);
        setLastUpdate(Date.now());
        setError(null);
      } catch (e) {
        if (!cancelled) setError("Session closed or unavailable");
      }
    };
    tick();
    const id = setInterval(tick, SESSION_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [api, picked.session_id]);

  const secSince = useMemo(
    () => (lastUpdate ? Math.round((Date.now() - lastUpdate) / 1000) : null),
    [lastUpdate],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[9px] uppercase tracking-[0.3em]" style={{ color: toolColor[picked.tool] }}>
            {toolLabel[picked.tool]} · Live
          </div>
          <div
            className="text-2xl font-bold uppercase tracking-tight text-white"
            style={{ fontFamily: "Chakra Petch, sans-serif" }}
          >
            {session?.name || picked.session_name}
          </div>
          <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#8B939A]">
            <User size={11} weight="bold" /> {picked.username}
            {secSince != null && (
              <>
                <span className="text-[#4A5054]">·</span>
                <span className="inline-flex items-center gap-1">
                  <ArrowClockwise
                    size={11}
                    weight="bold"
                    className={secSince <= SESSION_POLL_MS / 1000 + 1 ? "animate-spin" : ""}
                  />
                  Last update {secSince}s ago
                </span>
              </>
            )}
          </div>
        </div>
        <button
          data-testid="live-viewer-back"
          onClick={onBack}
          className="rounded-md border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
        >
          ← All sessions
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-[#FF9F0A] bg-[#FF9F0A]/10 px-3 py-2 text-[11px] text-[#FFC989]">
          {error}
        </div>
      )}

      {!session && !error && (
        <div className="p-8 text-center text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">
          Loading session…
        </div>
      )}

      {session && picked.tool === "ovality" && <OvalityViewer session={session} />}
      {session && picked.tool === "verticality" && <VerticalityViewer session={session} />}
    </div>
  );
};

// ---------- Public entry point -----------------------------------------

export default function LiveSessions({ onBack }) {
  const [picked, setPicked] = useState(null);

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6" data-testid="live-sessions-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E1C7]">Live Sessions</div>
          <div
            className="text-2xl font-bold uppercase tracking-tight text-white"
            style={{ fontFamily: "Chakra Petch, sans-serif" }}
          >
            {picked ? "Watching" : "Field roster"}
          </div>
        </div>
        {!picked && (
          <button
            data-testid="live-back-btn"
            onClick={onBack}
            className="rounded-md border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
          >
            ← Toolbox
          </button>
        )}
      </div>

      {picked ? (
        <SessionViewer picked={picked} onBack={() => setPicked(null)} />
      ) : (
        <SessionList onPick={setPicked} />
      )}
    </div>
  );
}

/**
 * useLiveCount — small hook exported for the Toolbox tile so it can
 * show a live "N active" badge without instantiating the whole
 * LiveSessions page. Polls the same endpoint but at a slower cadence
 * (5 s) since the badge doesn't need to be sub-second fresh.
 */
export function useLiveCount(pollMs = 5000) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await live.list();
        if (!cancelled) setCount(r.sessions?.length || 0);
      } catch {
        if (!cancelled) setCount(0);
      }
    };
    tick();
    const id = setInterval(tick, pollMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [pollMs]);
  return count;
}
